/* eslint-disable react/display-name */
import React, { useState, forwardRef, useImperativeHandle, useEffect, useCallback } from 'react';
import Api from '../../api/global.service';
import { useAuth } from '../../components/content/auth.jsx';
import * as yup from 'yup';
import { publish } from '../events';
import { useCoopConfig } from '../content/coop-config.jsx';

// Compontente para ingresar un precio definido
const InputPrice = ({ label, inputName, inputValue, onChange, currency, error }) => {
  return (
    <>
      <label className="font-open-normal-400 body-0 subtitle-1">{label}</label>
      <div className="input-group mb-2">
        <div className="input-group-prepend">
          <i className="fa fa-search" style={{ padding: 14, color: '#ffffff' }}></i>
        </div>
        <input
          type="number"
          name={inputName}
          className="form-control"
          value={inputValue[inputName]?.precio ?? 0}
          onChange={event => onChange(inputName, event.target.value, inputValue[inputName]?.precio_id)}
        />
        <div className="input-group-append">
          <span className="p-5 pl-0 font-open-normal-400 body-0 subtitle-1">{currency}</span>
        </div>
      </div>
      {error && <div className="invalid-feedback input-group border-0 mb-2">{error}</div>}
    </>
  );
};

// Componente principal
const ModalPrecioRemanente = forwardRef((props, ref) => {
  const auth = useAuth();
  const coopConfig = useCoopConfig();
  const [show, setShow] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [errors, setErrors] = useState({});
  const [categorias, setCategorias] = useState([]);
  const [values, setValues] = useState({});
  const [inputValue, setInputValue] = useState({});
  const [inputDefaultValue, setInputDefaultValue] = useState({});
  const [schema, setSchema] = useState(null);
  const yupInput = yup
    .number()
    .positive('Debe ser un número mayor que cero')
    // .required('Debe escribir una cantidad') // lo desactivamos porque inputValues es dinámico
    .transform(value => (isNaN(value) ? undefined : value));

  useEffect(() => {
    getCategorias();
  }, []);

  const getCategorias = async () => {
    const response = await Api.Acopio.get('remanente/categorias');
    setCategorias(response.data);
    // Yup dynamic
    const shape = {};
    response.data.forEach(c => {
      shape[c.id] = yup.object().shape({
        precio: yupInput,
      });
    });
    setSchema(yup.object().shape(shape));
  };

  //To call outside
  useImperativeHandle(ref, () => ({
    show: showHandler,
  }));

  const showHandler = valuesData => {
    setShow(true);
    setErrors(null);
    setValues(valuesData);
    defaultValues(valuesData);
  };

  const defaultValues = valuesData => {
    if (valuesData?.precioCategoria) {
      valuesData.precioCategoria.forEach(pc => {
        setCantidad(pc.id_cat_certificacion, parseFloat(pc.precio), pc.precio_id, 1);
      });
    }
  };

  const closeHandler = () => {
    setShow(false);
  };

  const validateHandler = () => {
    schema
      .validate(inputValue, { abortEarly: false })
      .then(function (valid) {
        // data post
        const dataCoop = { id_cooperativa: auth.user.id_cooperativa, id_ciclo: values.id_ciclo };
        let data = [];

        // exit if no data or changes
        if (Object.keys(inputValue).length === 0 || JSON.stringify(inputValue) === JSON.stringify(inputDefaultValue)) {
          setBlocking(false);
          setShow(false);
          return false;
        }

        // object data preparation
        Object.entries(inputValue).forEach(([id, inputVal]) => {
          const precioNum = Number(inputVal.precio);
          if (precioNum !== '' && !isNaN(precioNum) && precioNum !== 0) {
            data.push({ ...dataCoop, id_cat_certificacion: id, precio_por_kg: parseFloat(precioNum * 100), precio_id: inputVal.precio_id });
          }
        });

        // Post request
        if (data.length) {
          setBlocking(true);
          let registroPrecio = [];
          data.forEach(d => {
            d.precio_id
              ? registroPrecio.push(Api.Acopio.update('remanente/registro_precio', d.precio_id, d))
              : registroPrecio.push(Api.Acopio.create('remanente/registro_precio', d));
          });
          Promise.all(registroPrecio).then(values => {
            setBlocking(false);
            setShow(false);
            publish('Precio::onSave', { dataRes: values, dataCoop: dataCoop });
          });
        }
      })
      .catch(function (err) {
        let errors = err.inner.reduce(function (map, obj) {
          map[obj.path] = obj.message;
          return map;
        }, {});
        setErrors(errors);
      });
  };

  const setCantidad = (inputName, currentVal, precio_id = null, inputDefault = 0) => {
    const inputData = {
      precio: currentVal,
      precio_id: precio_id,
    };
    setInputValue(prev => ({
      ...prev,
      [inputName]: inputData,
    }));
    // save default value for submit data comparation
    if (inputDefault === 1) {
      setInputDefaultValue(prev => ({
        ...prev,
        [inputName]: inputData,
      }));
    }
  };

  return (
    <div className="modal" style={{ display: show ? 'block' : 'none', backgroundColor: 'rgba(0,0,0,0.2)' }}>
      <div className="modal-dialog" style={{ top: 40, right: 25, position: 'absolute', width: 400, height: 'calc(100% - 100px)' }}>
        <div className="modal-content" style={{ height: '100%' }}>
          <div className="modal-header" style={{ flex: 'inherit' }}>
            <h4 className="modal-title">{props.title}</h4>
            <button onClick={() => closeHandler()} type="button" className="btn" style={{ borderRadius: '20px 20px 20px 20px' }}>
              <i className="fa fa-times"></i>
            </button>
          </div>
          <div
            className="modal-body"
            style={{
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 200px)',
              paddingRight: '10px',
            }}>
            <div className="row my-4">
              <div className="col-12">
                {categorias.length > 0 && (
                  <>
                    {categorias?.map((c, i) => (
                      <div key={i}>
                        <InputPrice
                          label={c.nombre}
                          inputName={c.id}
                          inputValue={inputValue}
                          currency={`${coopConfig.currency()?.symbol}/${coopConfig.hasAcopioUnitsExtra() ? coopConfig.acopioUnitsExtra()?.abreviacion : coopConfig.acopioUnits()?.abreviacion}`}
                          onChange={setCantidad}
                          error={errors?.[c.id + '.precio']}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            {blocking && (
              <div className="spinner-border text-warning" role="status">
                <span className="sr-only">Guardando...</span>
              </div>
            )}
            {!blocking && (
              <>
                <button
                  onClick={() => closeHandler()}
                  type="button"
                  className="btn btn-outline-secondary btn-sirio"
                  style={{ borderRadius: '20px 20px 20px 20px' }}>
                  <i className="fa fa-times"></i> Cancelar
                </button>
                <button
                  onClick={() => validateHandler()}
                  type="button"
                  className="btn btn-outline-secondary btn-sirio"
                  style={{ borderRadius: '20px 20px 20px 20px' }}>
                  <i className="fa fa-save"></i> Guardar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default ModalPrecioRemanente;
